
import React, { useState, useEffect } from 'react';
import { ChevronRight, ArrowLeft, Utensils, Search, Menu as MenuIcon, Instagram, Facebook, MapPin, Phone, Minus, Plus, Star } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const ALLERGEN_MAP = {
    gluten: { label: 'Gluten', emoji: '🌾' },
    dairy: { label: 'Sut', emoji: '🥛' },
    eggs: { label: 'Yumurta', emoji: '🥚' },
    nuts: { label: 'Kabuklu Yemis', emoji: '🥜' },
    soy: { label: 'Soya', emoji: '🫘' },
    fish: { label: 'Balık', emoji: '🐟' },
    shellfish: { label: 'Kabuklu Deniz', emoji: '🦐' },
    sesame: { label: 'Susam', emoji: '⚪' },
};

const AllergenBadges = ({ allergens, size = 'normal' }) => {
    if (!allergens?.length) return null;
    const isSmall = size === 'small';
    return (
        <div style={{ marginTop: isSmall ? '6px' : '8px', paddingTop: isSmall ? '6px' : '8px', borderTop: '1px dashed rgba(245,158,11,0.25)' }}>
            <div style={{ fontSize: isSmall ? '0.6rem' : '0.65rem', fontWeight: 700, color: '#b45309', marginBottom: isSmall ? '4px' : '5px', textTransform: 'uppercase', letterSpacing: '0.03em', opacity: 0.8 }}>
                Alerjenler
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: isSmall ? '3px' : '4px' }}>
                {allergens.map(id => {
                    const info = ALLERGEN_MAP[id];
                    if (!info) return null;
                    return (
                        <span key={id} title={info.label} style={{
                            display: 'inline-flex', alignItems: 'center', gap: '3px',
                            padding: isSmall ? '1px 6px' : '2px 8px',
                            borderRadius: '10px', fontSize: isSmall ? '0.6rem' : '0.65rem',
                            background: 'rgba(245,158,11,0.1)', color: '#b45309',
                            fontWeight: 600, lineHeight: 1.4,
                            border: '1px solid rgba(245,158,11,0.2)',
                        }}>
                            <span style={{ fontSize: isSmall ? '0.65rem' : '0.7rem' }}>{info.emoji}</span>
                            {info.label}
                        </span>
                    );
                })}
            </div>
        </div>
    );
};

const ItemDetailModal = ({ item, onClose, onAddToCart, onUpdateQuantity, cart, primaryColor, tenantPlan, t }) => {
    if (!item) return null;
    const prodId = `${item.categoryName}_${item.name}`;
    const cartItem = cart.find(i => i.prodId === prodId);
    const canOrder = tenantPlan === 'pro' || tenantPlan === 'premium';

    return (
        <div
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1000, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}
            onClick={onClose}
        >
            <div
                style={{ background: 'var(--bg-card, #fff)', borderRadius: '24px 24px 0 0', maxWidth: '600px', width: '100%', maxHeight: '88vh', overflowY: 'auto', paddingBottom: '2rem' }}
                onClick={e => e.stopPropagation()}
            >
                <div style={{ padding: '12px 0 0', textAlign: 'center' }}>
                    <div style={{ width: '40px', height: '4px', background: '#e5e7eb', borderRadius: '2px', display: 'inline-block' }} />
                </div>
                {item.image && (
                    <div style={{ width: '100%', height: '240px', overflow: 'hidden', marginBottom: '1.25rem' }}>
                        <img src={item.image} alt={item.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    </div>
                )}
                <div style={{ padding: '0 1.5rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                        <h2 style={{ fontSize: '1.375rem', fontWeight: 900, margin: 0, flex: 1, paddingRight: '1rem', color: 'var(--text-main, #1a1a1a)', wordBreak: 'break-word' }}>{item.name}</h2>
                        <span style={{ fontSize: '1.375rem', fontWeight: 900, color: primaryColor, flexShrink: 0 }}>{item.price} ₺</span>
                    </div>
                    {item.description && (
                        <p style={{ fontSize: '0.875rem', color: 'var(--text-muted, #666)', lineHeight: 1.6, marginBottom: '0.75rem', wordBreak: 'break-word' }}>{item.description}</p>
                    )}
                    {item.calories && (
                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', background: 'rgba(245,158,11,0.1)', color: '#b45309', padding: '4px 12px', borderRadius: '20px', fontSize: '0.75rem', fontWeight: 700, marginBottom: '0.75rem' }}>
                            🔥 {item.calories} kcal
                        </div>
                    )}
                    {item.detailedDescription && (
                        <div style={{ background: 'var(--bg-main, #f5f5f5)', borderRadius: '12px', padding: '1rem', marginBottom: '0.75rem' }}>
                            <div style={{ fontSize: '0.6rem', fontWeight: 700, color: 'var(--text-muted, #666)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>Detaylı Açıklama</div>
                            <p style={{ fontSize: '0.875rem', lineHeight: 1.7, margin: 0, color: 'var(--text-main, #1a1a1a)', wordBreak: 'break-word' }}>{item.detailedDescription}</p>
                        </div>
                    )}
                    <AllergenBadges allergens={item.allergens} />
                    {item.customAllergens && (
                        <div style={{ marginTop: '8px', fontSize: '0.75rem', color: '#b45309', fontWeight: 600 }}>⚠️ {item.customAllergens}</div>
                    )}
                    {canOrder && (
                        <div style={{ marginTop: '1.25rem' }}>
                            {cartItem ? (
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1rem', background: 'var(--bg-main, #f5f5f5)', padding: '10px', borderRadius: '14px' }}>
                                    <button onClick={() => onUpdateQuantity && onUpdateQuantity(prodId, -1)} style={{ background: primaryColor, color: 'white', border: 'none', borderRadius: '10px', width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}><Minus size={18} /></button>
                                    <span style={{ fontWeight: 900, fontSize: '1.125rem', minWidth: '32px', textAlign: 'center', color: 'var(--text-main, #1a1a1a)' }}>{cartItem.quantity}</span>
                                    <button onClick={() => onAddToCart && onAddToCart({ ...item })} style={{ background: primaryColor, color: 'white', border: 'none', borderRadius: '10px', width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}><Plus size={18} /></button>
                                </div>
                            ) : (
                                <button
                                    onClick={() => onAddToCart && onAddToCart({ ...item })}
                                    style={{ width: '100%', padding: '14px', background: primaryColor, color: 'white', border: 'none', borderRadius: '14px', fontSize: '1rem', fontWeight: 800, cursor: 'pointer' }}
                                >
                                    + {t('portal.addToCart')}
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const DigitalMenu = ({ menuData, inventory = {}, primaryColor: globalPrimaryColor, onAddToCart, onUpdateQuantity, cart = [], cartCount, tenantPlan = 'pro', tenant, hideHeader = false }) => {
    const { t } = useTranslation();
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedItem, setSelectedItem] = useState(null);

    const design = menuData?.designRecommendation || {};
    const primaryColor = design.primaryColor || globalPrimaryColor || '#6f4e37';
    
    // Unified Font Family Logic
    let fontFamily = "";
    switch(design.fontStyle) {
        case 'serif': fontFamily = "'Georgia', serif"; break;
        case 'playfair': fontFamily = "'Playfair Display', serif"; break;
        case 'outfit': fontFamily = "'Outfit', sans-serif"; break;
        case 'montserrat': fontFamily = "'Montserrat', sans-serif"; break;
        default: fontFamily = "'Inter', system-ui, sans-serif";
    }

    const template = design.template || 'classic';
    const textColor = design.textColor || '#2c1a12';
    const cardBgColor = design.cardBgColor || '#ffffff';
    const allCategories = menuData.categories || [];

    // Handle Native Back Gesture
    useEffect(() => {
        const handlePopState = () => {
            if (selectedCategory) {
                setSelectedCategory(null);
            }
        };
        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, [selectedCategory]);

    const handleCategoryClick = (catName) => {
        window.history.pushState({ view: 'category', name: catName }, '');
        setSelectedCategory(catName);
    };

    const handleManualBack = () => {
        window.history.back();
    };

    const handleManualItemClick = (item, prodId, isOutOfStock) => {
        if (isOutOfStock) return;
        setSelectedItem({ ...item });
    };

    if (!menuData) {
        return (
            <div style={{ fontFamily: fontFamily, maxWidth: '600px', margin: '0 auto', width: '100%', '--text-main': textColor, '--text-muted': `${textColor}b3`, color: textColor, '--bg-card': cardBgColor }}>
                <div className="card glass-premium" style={{ padding: '3rem', textAlign: 'center', opacity: 0.5 }}>
                    <Utensils size={48} style={{ margin: '0 auto 1rem', color: primaryColor }} />
                    <p>{t('menu.noMenus', 'Menü henüz eklenmemiş.')}</p>
                </div>
                <MenuFooter tenant={tenant} primaryColor={primaryColor} textColor={textColor} />
            </div>
        );
    }

    const filteredCategories = (menuData.categories || []).filter(cat =>
        cat && (
            (cat.categoryName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
            (Array.isArray(cat.items) ? cat.items : []).some(item => item && (item.name || '').toLowerCase().includes(searchTerm.toLowerCase()))
        )
    );

    // Helper: Get items to display
    const getDisplayedItems = () => {
        let displayedItems = [];
        if (searchTerm) {
            filteredCategories.forEach(cat => {
                const searchMatches = (Array.isArray(cat.items) ? cat.items : []).filter(item =>
                    item && (
                        (item.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                        (item.description || '').toLowerCase().includes(searchTerm.toLowerCase())
                    )
                ).map(item => ({ ...item, categoryName: cat.categoryName }));
                displayedItems = [...displayedItems, ...searchMatches];
            });
        } else if (selectedCategory) {
            const category = menuData.categories?.find(c => c.categoryName === selectedCategory);
            if (category) {
                displayedItems = (Array.isArray(category.items) ? category.items : []).map(item => ({
                    ...item,
                    categoryName: category.categoryName
                }));
            }
        }
        return displayedItems;
    };

    // Helper: Get featured items
    const getFeaturedItems = () => {
        let featured = [];
        allCategories.forEach((cat, cIdx) => {
            (Array.isArray(cat.items) ? cat.items : []).forEach((item, iIdx) => {
                if (item.featured) featured.push({ ...item, categoryName: cat.categoryName, cIdx, iIdx });
            });
        });

        const order = design.featuredOrder || [];
        if (order.length > 0) {
            featured.sort((a, b) => {
                const aIdx = order.indexOf(`${a.cIdx}-${a.iIdx}`);
                const bIdx = order.indexOf(`${b.cIdx}-${b.iIdx}`);
                if (aIdx === -1 && bIdx === -1) return 0;
                if (aIdx === -1) return 1;
                if (bIdx === -1) return -1;
                return aIdx - bIdx;
            });
        }
        
        return featured;
    };

    const featuredItems = getFeaturedItems();
    const featuredTitle = design.featuredTitle || (template === 'elegant' ? 'Öne Çıkanlar' : t('portal.featured', 'Öne Çıkanlar'));

    // Helper: Stock check
    const getStockInfo = (item) => {
        const prodId = `${item.categoryName}_${item.name}`;
        const stock = inventory[prodId] ?? 999;
        const isOutOfStock = tenant?.stock_menu_enabled ? (stock !== -1 && stock <= 0) : false;
        return { prodId, isOutOfStock };
    };

    // Helper: Cart button renderer
    const renderCartButton = (item, prodId, isOutOfStock, variant = 'default') => {
        if (tenantPlan !== 'pro' && tenantPlan !== 'premium') return null;
        const cartItem = cart.find(i => i.prodId === prodId);
        if (cartItem) {
            return (
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', background: 'var(--bg-card)', padding: '4px 8px', borderRadius: '10px', border: '1px solid var(--border)', width: 'fit-content', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                    <button onClick={() => onUpdateQuantity && onUpdateQuantity(prodId, -1)} style={{ background: 'transparent', border: 'none', color: primaryColor, padding: '4px', cursor: 'pointer', display: 'flex' }}><Minus size={16} /></button>
                    <span style={{ fontWeight: 800, fontSize: '0.9rem', minWidth: '20px', textAlign: 'center', color: 'var(--text-main)' }}>{cartItem.quantity}</span>
                    <button onClick={() => onUpdateQuantity && onUpdateQuantity(prodId, 1)} style={{ background: 'transparent', border: 'none', color: primaryColor, padding: '4px', cursor: 'pointer', display: 'flex' }}><Plus size={16} /></button>
                </div>
            );
        }

        if (variant === 'text-only') {
            return (
                <button
                    onClick={() => !isOutOfStock && onAddToCart && onAddToCart({ ...item, categoryName: item.categoryName })}
                    disabled={isOutOfStock}
                    style={{
                        background: 'transparent',
                        color: isOutOfStock ? 'var(--text-muted)' : primaryColor,
                        border: 'none',
                        padding: '0', 
                        fontSize: '0.8125rem', 
                        fontWeight: 600,
                        cursor: isOutOfStock ? 'not-allowed' : 'pointer', 
                        transition: 'opacity 0.2s',
                        opacity: isOutOfStock ? 0.7 : 1
                    }}
                    onMouseOver={(e) => !isOutOfStock && (e.target.style.opacity = '0.7')}
                    onMouseOut={(e) => !isOutOfStock && (e.target.style.opacity = '1')}
                >
                    {isOutOfStock ? t('inventory.outOfStock', 'Stokta Yok') : `+ ${t('portal.addToCart')}`}
                </button>
            );
        }

        return (
            <button
                onClick={() => !isOutOfStock && onAddToCart && onAddToCart({ ...item, categoryName: item.categoryName })}
                disabled={isOutOfStock}
                style={{
                    background: isOutOfStock ? 'var(--border)' : 'transparent',
                    color: isOutOfStock ? 'var(--text-muted)' : primaryColor,
                    border: isOutOfStock ? 'none' : `1.5px solid ${primaryColor}40`,
                    borderRadius: '10px', padding: '6px 16px', fontSize: '0.8125rem', fontWeight: 700,
                    cursor: isOutOfStock ? 'not-allowed' : 'pointer', transition: 'all 0.2s'
                }}
                onMouseOver={(e) => !isOutOfStock && (e.target.style.background = `${primaryColor}15`)}
                onMouseOut={(e) => !isOutOfStock && (e.target.style.background = 'transparent')}
            >
                {isOutOfStock ? t('inventory.outOfStock', 'Stokta Yok') : `+ ${t('portal.addToCart')}`}
            </button>
        );
    };

    // ═══════════════════════════════════════════
    // TEMPLATE: COMPACT (Liste görünümü)
    // ═══════════════════════════════════════════
    if (template === 'compact') {
        const allCategories = menuData.categories || [];
        const activeCat = selectedCategory || (allCategories[0]?.categoryName);
        const currentCategory = allCategories.find(c => c.categoryName === activeCat);
        const items = searchTerm ? getDisplayedItems() : (currentCategory ? (currentCategory.items || []).map(i => ({ ...i, categoryName: currentCategory.categoryName })) : []);

        return (
            <div className="fade-in" style={{ fontFamily: fontFamily, maxWidth: '600px', margin: '0 auto', width: '100%', '--text-main': textColor, '--text-muted': `${textColor}b3`, color: textColor, '--bg-card': cardBgColor }}>
                <style>{`
                    @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;700;900&family=Outfit:wght@400;700;900&family=Playfair+Display:ital,wght@0,400;0,700;0,900;1,400&display=swap');
                    * { font-family: inherit; }
                `}</style>
                {/* Header */}
                {!hideHeader && (
                    <div style={{ textAlign: 'center', padding: '1rem 0 1.25rem', borderBottom: '2px solid var(--border)' }}>
                        <h1 style={{ fontSize: '1.5rem', fontWeight: 900, color: 'var(--text-main)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px' }}>
                            <Utensils size={18} style={{ color: primaryColor, opacity: 0.8 }} />
                            {menuData.menuName}
                            <Utensils size={18} style={{ color: primaryColor, opacity: 0.8 }} />
                        </h1>
                        {menuData.description && <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>{menuData.description}</p>}
                    </div>
                )}

                {/* Category tabs - horizontal scroll */}
                <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none', padding: '1rem 0', borderBottom: '1px solid var(--border)' }}>
                    <style>{`.compact-cats::-webkit-scrollbar { display: none; }`}</style>
                    <div className="compact-cats" style={{ display: 'flex', gap: '0.5rem', minWidth: 'max-content' }}>
                        {allCategories.map((cat, idx) => {
                            const isActive = cat.categoryName === activeCat && !searchTerm;
                            return (
                                <button key={idx} onClick={() => { setSelectedCategory(cat.categoryName); setSearchTerm(''); }}
                                    style={{
                                        padding: '0.5rem 1rem', borderRadius: '100px', border: 'none', whiteSpace: 'nowrap',
                                        background: isActive ? 'var(--primary)' : 'var(--bg-card)',
                                        color: isActive ? 'white' : 'var(--text-main)',
                                        fontWeight: 700, fontSize: '0.8125rem', cursor: 'pointer', transition: 'all 0.2s',
                                        boxShadow: isActive ? '0 4px 12px rgba(0,0,0,0.15)' : 'none'
                                    }}
                                >
                                    {cat.image && <img src={cat.image} alt="" style={{ width: '20px', height: '20px', borderRadius: '50%', objectFit: 'cover', marginRight: '6px', verticalAlign: 'middle' }} />}
                                    {cat.categoryName}
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Search */}
                <div style={{ padding: '0.75rem 0' }}>
                    <div style={{ position: 'relative' }}>
                        <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                        <input type="text" placeholder={t('common.search')} value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                            style={{ width: '100%', padding: '10px 12px 10px 40px', border: '1px solid var(--border)', borderRadius: '12px', background: 'var(--bg-card)', color: 'var(--text-main)', fontSize: '0.875rem', outline: 'none' }}
                        />
                    </div>
                </div>

                {/* Category title */}
                {!searchTerm && activeCat && (
                    <div style={{ padding: '0.75rem 0 0.5rem', borderBottom: '1px solid var(--border)', marginBottom: '0.5rem' }}>
                        <h2 style={{ fontSize: '1.125rem', fontWeight: 800 }}>{activeCat}</h2>
                    </div>
                )}

                {/* Items list - compact rows */}
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                    {items.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '3rem', opacity: 0.5 }}>{t('common.noResults')}</div>
                    ) : items.map((item, idx) => {
                        const { prodId, isOutOfStock } = getStockInfo(item);
                        return (
                            <div key={`${prodId}-${idx}`}
                                onClick={() => !isOutOfStock && setSelectedItem({ ...item })}
                                style={{
                                    display: 'flex', alignItems: 'center', gap: '1rem', padding: '0.875rem 0.5rem',
                                    borderBottom: '1px solid var(--border)', opacity: isOutOfStock ? 0.5 : 1,
                                    transition: 'background 0.2s', cursor: isOutOfStock ? 'default' : 'pointer'
                                }}>
                                {item.image && (
                                    <div style={{ width: '72px', height: '72px', borderRadius: '12px', overflow: 'hidden', flexShrink: 0, position: 'relative', background: 'var(--bg-main)' }}>
                                        <img src={item.image} alt={item.name} style={{ width: '100%', height: '100%', objectFit: 'cover', filter: isOutOfStock ? 'grayscale(100%)' : 'none' }} />
                                        {isOutOfStock && (
                                            <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '9px', fontWeight: 800 }}>
                                                {t('inventory.outOfStock', 'Stokta Yok')}
                                            </div>
                                        )}
                                    </div>
                                )}
                                <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                        <div style={{ flex: 1, paddingRight: '1rem' }}>
                                            <h4 style={{ fontSize: '1rem', fontWeight: 800, color: 'var(--text-main)', margin: '0 0 2px 0' }}>{item.name}</h4>
                                            {item.description && <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', lineHeight: 1.4, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', margin: 0 }}>{item.description}</p>}
                                        </div>
                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', flexShrink: 0 }}>
                                            <div style={{ fontWeight: 900, fontSize: '1.1rem', color: primaryColor }}>{item.price} ₺</div>
                                            <div style={{ marginTop: '6px' }}>
                                                {renderCartButton(item, prodId, isOutOfStock, 'text-only')}
                                            </div>
                                        </div>
                                    </div>
                                    <AllergenBadges allergens={item.allergens} size="small" />
                                </div>
                            </div>
                        );
                    })}
                </div>

                <MenuFooter tenant={tenant} primaryColor={primaryColor} textColor={textColor} />
                <ItemDetailModal item={selectedItem} onClose={() => setSelectedItem(null)} onAddToCart={onAddToCart} onUpdateQuantity={onUpdateQuantity} cart={cart} primaryColor={primaryColor} tenantPlan={tenantPlan} t={t} />
            </div>
        );
    }

    // ═══════════════════════════════════════════
    // TEMPLATE: ELEGANT (Tek sütun, büyük görseller)
    // ═══════════════════════════════════════════
    if (template === 'elegant') {
        const allCategories = menuData.categories || [];

        // Landing: show all categories with featured items
        if (!selectedCategory && !searchTerm) {
            return (
                <div className="fade-in" style={{ fontFamily: fontFamily, maxWidth: '600px', margin: '0 auto', width: '100%', '--text-main': textColor, '--text-muted': `${textColor}b3`, color: textColor, '--bg-card': cardBgColor }}>
                    <style>{`
                        @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;700;900&family=Outfit:wght@400;700;900&family=Playfair+Display:ital,wght@0,400;0,700;0,900;1,400&display=swap');
                        * { font-family: inherit; }
                    `}</style>
                    {/* Header */}
                    {!hideHeader && (
                        <div style={{ textAlign: 'center', padding: '1.5rem 1rem 2rem' }}>
                            <div style={{ width: '48px', height: '3px', background: primaryColor, margin: '0 auto 1rem', borderRadius: '2px' }}></div>
                            <h1 style={{ fontSize: '1.75rem', fontWeight: 900, letterSpacing: '3px', textTransform: 'uppercase', color: 'var(--text-main)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px' }}>
                                <Utensils size={18} style={{ color: primaryColor }} />
                                {menuData.menuName}
                                <Utensils size={18} style={{ color: primaryColor }} />
                            </h1>
                            {menuData.description && <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', fontStyle: 'italic', marginTop: '0.5rem' }}>{menuData.description}</p>}
                        </div>
                    )}

                    {/* Search */}
                    <div className="card glass-premium" style={{ padding: '0.875rem', marginBottom: '2rem', background: 'var(--bg-card)' }}>
                        <div style={{ position: 'relative' }}>
                            <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                            <input type="text" placeholder={t('common.search')} value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                                style={{ paddingLeft: '40px', background: 'transparent', border: 'none', fontSize: '0.875rem', width: '100%', outline: 'none', color: 'inherit' }}
                            />
                        </div>
                    </div>

                    {/* Featured items */}
                    {featuredItems.length > 0 && (
                        <div style={{ marginBottom: '2.5rem' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '1rem' }}>
                                <Star size={16} style={{ color: '#f59e0b' }} />
                                <h3 style={{ fontSize: '0.875rem', fontWeight: 800, letterSpacing: '2px', color: 'var(--text-muted)' }}>{featuredTitle}</h3>
                            </div>
                            <div style={{ display: 'flex', gap: '1rem', overflowX: 'auto', WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none', paddingBottom: '0.5rem' }}>
                                <style>{`.elegant-featured::-webkit-scrollbar { display: none; }`}</style>
                                <div className="elegant-featured" style={{ display: 'flex', gap: '1rem' }}>
                                    {featuredItems.map((item, idx) => {
                                        const { prodId, isOutOfStock } = getStockInfo(item);
                                        return (
                                            <div key={idx} onClick={() => handleManualItemClick(item, prodId, isOutOfStock)}
                                                style={{ width: '160px', flexShrink: 0, cursor: 'pointer', borderRadius: '20px', overflow: 'hidden', background: 'var(--bg-card)', boxShadow: '0 4px 20px rgba(0,0,0,0.06)' }}>
                                                {item.image && <img src={item.image} alt={item.name} style={{ width: '100%', height: '120px', objectFit: 'cover' }} />}
                                                <div style={{ padding: '0.75rem' }}>
                                                    <div style={{ fontSize: '0.8125rem', fontWeight: 800, marginBottom: '2px' }}>{item.name}</div>
                                                    <div style={{ fontSize: '0.875rem', fontWeight: 900, color: primaryColor }}>{item.price} ₺</div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Categories - elegant single column cards */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                        {filteredCategories.map((cat, idx) => (
                            <div key={idx} className="fade-in-up" onClick={() => handleCategoryClick(cat.categoryName)}
                                style={{
                                    borderRadius: '20px', overflow: 'hidden', cursor: 'pointer', position: 'relative',
                                    height: '140px', animationDelay: `${idx * 0.05}s`
                                }}>
                                <img src={cat.image || 'https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?w=600&h=300&fit=crop'} alt={cat.categoryName}
                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                <div style={{
                                    position: 'absolute', inset: 0,
                                    background: 'linear-gradient(to right, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.2) 100%)',
                                    display: 'flex', alignItems: 'center', padding: '0 2rem'
                                }}>
                                    <div style={{ minWidth: 0 }}>
                                        <h3 style={{ color: 'white', fontSize: '1.05rem', fontWeight: 900, letterSpacing: '0.5px', margin: 0, minWidth: 0, overflowWrap: 'anywhere', wordBreak: 'break-word' }}>{cat.categoryName}</h3>
                                        <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.8125rem', marginTop: '4px' }}>
                                            {(Array.isArray(cat.items) ? cat.items : []).length} ürün
                                        </div>
                                    </div>
                                    <ChevronRight size={24} style={{ color: 'white', marginLeft: 'auto', opacity: 0.6, flexShrink: 0 }} />
                                </div>
                            </div>
                        ))}
                    </div>

                    <MenuFooter tenant={tenant} primaryColor={primaryColor} textColor={textColor} />
                    <ItemDetailModal item={selectedItem} onClose={() => setSelectedItem(null)} onAddToCart={onAddToCart} onUpdateQuantity={onUpdateQuantity} cart={cart} primaryColor={primaryColor} tenantPlan={tenantPlan} t={t} />
                </div>
            );
        }

        // Items view for elegant
        const displayedItems = getDisplayedItems();
        const categoryTitle = selectedCategory || t('common.searchResults');

        return (
            <div className="fade-in" style={{ fontFamily: fontFamily, maxWidth: '600px', margin: '0 auto', width: '100%', '--text-main': textColor, '--text-muted': `${textColor}b3`, color: textColor }}>
                <style>{`
                    @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;700;900&family=Outfit:wght@400;700;900&family=Playfair+Display:ital,wght@0,400;0,700;0,900;1,400&display=swap');
                    * { font-family: inherit; }
                `}</style>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
                    <button onClick={handleManualBack} style={{ background: 'var(--bg-card)', border: 'none', padding: '10px', borderRadius: '12px', display: 'flex', alignItems: 'center', color: 'var(--text-main)', boxShadow: 'var(--shadow-sm)' }}>
                        <ArrowLeft size={18} />
                    </button>
                    <h2 style={{ fontSize: '1.25rem', fontWeight: 800 }}>{categoryTitle}</h2>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                    {displayedItems.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '3rem', opacity: 0.5 }}>{t('common.noResults')}</div>
                    ) : displayedItems.map((item, idx) => {
                        const { prodId, isOutOfStock } = getStockInfo(item);
                        return (
                            <div key={`${prodId}-${idx}`}
                                onClick={() => !isOutOfStock && setSelectedItem({ ...item })}
                                style={{
                                    borderRadius: '20px', overflow: 'hidden', background: 'var(--bg-card)',
                                    boxShadow: '0 4px 20px rgba(0,0,0,0.06)', opacity: isOutOfStock ? 0.6 : 1,
                                    cursor: isOutOfStock ? 'default' : 'pointer'
                                }}>
                                {item.image && (
                                    <div style={{ position: 'relative' }}>
                                        <img src={item.image} alt={item.name} style={{ width: '100%', height: '200px', objectFit: 'cover', filter: isOutOfStock ? 'grayscale(100%)' : 'none' }} />
                                        {isOutOfStock && (
                                            <div style={{ position: 'absolute', top: '12px', right: '12px', background: 'rgba(0,0,0,0.7)', color: 'white', padding: '4px 12px', borderRadius: '100px', fontSize: '0.75rem', fontWeight: 700 }}>
                                                {t('inventory.outOfStock', 'Stokta Yok')}
                                            </div>
                                        )}
                                    </div>
                                )}
                                <div style={{ padding: '1.25rem', flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                        <div style={{ flex: 1, paddingRight: '1rem' }}>
                                            <h4 style={{ fontSize: '1.25rem', fontWeight: 900, color: 'var(--text-main)', margin: '0 0 0.25rem 0' }}>{item.name}</h4>
                                            {item.description && <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', lineHeight: 1.5, margin: 0, wordBreak: 'break-word' }}>{item.description}</p>}
                                        </div>
                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', flexShrink: 0 }}>
                                            <span style={{ fontWeight: 800, fontSize: '1.25rem', color: primaryColor }}>{item.price} ₺</span>
                                            <div style={{ marginTop: '0.5rem' }} onClick={e => e.stopPropagation()}>
                                                {renderCartButton(item, prodId, isOutOfStock, 'text-only')}
                                            </div>
                                        </div>
                                    </div>
                                    <AllergenBadges allergens={item.allergens} />
                                </div>
                            </div>
                        );
                    })}
                </div>

                <button onClick={() => setSelectedCategory(null)} style={{ position: 'fixed', bottom: '80px', right: '25px', width: '60px', height: '60px', borderRadius: '50%', background: primaryColor, color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 10px 25px -5px rgba(0,0,0,0.3)', border: 'none', zIndex: 100 }}>
                    <MenuIcon size={24} />
                </button>

                <MenuFooter tenant={tenant} primaryColor={primaryColor} textColor={textColor} />
                <ItemDetailModal item={selectedItem} onClose={() => setSelectedItem(null)} onAddToCart={onAddToCart} onUpdateQuantity={onUpdateQuantity} cart={cart} primaryColor={primaryColor} tenantPlan={tenantPlan} t={t} />
            </div>
        );
    }

    // ═══════════════════════════════════════════
    // TEMPLATE: MINIMAL (Sade tipografi, görselsiz)
    // ═══════════════════════════════════════════
    if (template === 'minimal') {
        const allCategories = menuData.categories || [];

        // Show all categories inline (no category selection needed)
        if (!searchTerm) {
            return (
                <div className="fade-in" style={{ fontFamily: fontFamily, maxWidth: '600px', margin: '0 auto', width: '100%', '--text-main': textColor, '--text-muted': `${textColor}b3`, color: textColor, '--bg-card': cardBgColor }}>
                    <style>{`
                        @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;700;900&family=Outfit:wght@400;700;900&family=Playfair+Display:ital,wght@0,400;0,700;0,900;1,400&display=swap');
                        * { font-family: inherit; }
                    `}</style>
                    {/* Elegant minimal header */}
                    {!hideHeader && (
                        <div style={{ textAlign: 'center', padding: '2rem 1rem 1.5rem', borderBottom: '2px solid var(--text-main)', marginBottom: '1.5rem' }}>
                            <h1 style={{ fontSize: '2rem', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '6px', color: 'var(--text-main)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px' }}>
                                <Utensils size={20} />
                                {menuData.menuName}
                                <Utensils size={20} />
                            </h1>
                            {menuData.description && <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginTop: '0.5rem', fontStyle: 'italic' }}>{menuData.description}</p>}
                        </div>
                    )}

                    {/* Search */}
                    <div style={{ marginBottom: '2rem' }}>
                        <div style={{ position: 'relative' }}>
                            <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                            <input type="text" placeholder={t('common.search')} value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                                style={{ width: '100%', padding: '10px 12px 10px 40px', borderBottom: '1px solid var(--border)', borderLeft: 'none', borderRight: 'none', borderTop: 'none', borderRadius: '0', background: 'transparent', color: 'var(--text-main)', fontSize: '0.875rem', outline: 'none' }}
                            />
                        </div>
                    </div>

                    {/* All categories rendered inline */}
                    {allCategories.map((cat, catIdx) => (
                        <div key={catIdx} style={{ marginBottom: '2.5rem' }}>
                            <div style={{ borderBottom: '1px solid var(--border)', paddingBottom: '0.5rem', marginBottom: '1rem' }}>
                                <h2 style={{ fontSize: '1.25rem', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '3px', color: 'var(--text-main)' }}>{cat.categoryName}</h2>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                                {(Array.isArray(cat.items) ? cat.items : []).map((item, idx) => {
                                    const { prodId, isOutOfStock } = getStockInfo({ ...item, categoryName: cat.categoryName });
                                    return (
                                        <div key={idx}
                                            onClick={() => !isOutOfStock && setSelectedItem({ ...item, categoryName: cat.categoryName })}
                                            style={{ padding: '0.5rem 0', opacity: isOutOfStock ? 0.4 : 1, cursor: isOutOfStock ? 'default' : 'pointer' }}>
                                            <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem' }}>
                                                <h4 style={{ fontSize: '0.9375rem', fontWeight: 800, color: 'var(--text-main)' }}>{item.name}</h4>
                                                <div style={{ flex: 1, borderBottom: '1px dotted var(--border)', margin: '0 4px', minWidth: '20px', alignSelf: 'flex-end', marginBottom: '4px' }}></div>
                                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', flexShrink: 0 }}>
                                                    <span style={{ fontWeight: 900, fontSize: '1rem', color: primaryColor }}>{item.price} ₺</span>
                                                    {(tenantPlan === 'pro' || tenantPlan === 'premium') && (
                                                        <div style={{ marginTop: '4px' }}>
                                                            {renderCartButton({ ...item, categoryName: cat.categoryName }, prodId, isOutOfStock, 'text-only')}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            {item.description && <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', lineHeight: 1.4, marginTop: '4px', paddingRight: '60px' }}>{item.description}</p>}
                                            <AllergenBadges allergens={item.allergens} size="small" />
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ))}

                    <MenuFooter tenant={tenant} primaryColor={primaryColor} textColor={textColor} />
                    <ItemDetailModal item={selectedItem} onClose={() => setSelectedItem(null)} onAddToCart={onAddToCart} onUpdateQuantity={onUpdateQuantity} cart={cart} primaryColor={primaryColor} tenantPlan={tenantPlan} t={t} />
                </div>
            );
        }

        // Search results for minimal
        const displayedItems = getDisplayedItems();
        return (
            <div className="fade-in" style={{ fontFamily: fontFamily, maxWidth: '600px', margin: '0 auto', width: '100%', '--text-main': textColor, '--text-muted': `${textColor}b3`, color: textColor }}>
                <style>{`
                    @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;700;900&family=Outfit:wght@400;700;900&family=Playfair+Display:ital,wght@0,400;0,700;0,900;1,400&display=swap');
                    * { font-family: inherit; }
                `}</style>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
                    <button onClick={() => setSearchTerm('')} style={{ background: 'var(--bg-card)', border: 'none', padding: '10px', borderRadius: '12px', display: 'flex', alignItems: 'center', color: 'var(--text-main)' }}>
                        <ArrowLeft size={18} />
                    </button>
                    <div style={{ position: 'relative', flex: 1 }}>
                        <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                        <input type="text" placeholder={t('common.search')} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} autoFocus
                            style={{ width: '100%', padding: '10px 12px 10px 40px', border: '1px solid var(--border)', borderRadius: '12px', background: 'var(--bg-card)', color: 'var(--text-main)', fontSize: '0.875rem', outline: 'none' }}
                        />
                    </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    {displayedItems.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '3rem', opacity: 0.5 }}>{t('common.noResults')}</div>
                    ) : displayedItems.map((item, idx) => {
                        const { prodId, isOutOfStock } = getStockInfo(item);
                        return (
                            <div key={`${prodId}-${idx}`}
                                onClick={() => !isOutOfStock && setSelectedItem({ ...item })}
                                style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem', padding: '0.5rem 0', borderBottom: '1px solid var(--border)', opacity: isOutOfStock ? 0.4 : 1, cursor: isOutOfStock ? 'default' : 'pointer' }}>
                                <h4 style={{ fontSize: '0.9375rem', fontWeight: 800 }}>{item.name}</h4>
                                <div style={{ flex: 1, borderBottom: '1px dotted var(--border)', margin: '0 4px', minWidth: '20px', alignSelf: 'flex-end', marginBottom: '4px' }}></div>
                                <span style={{ fontWeight: 900, fontSize: '1rem', flexShrink: 0, color: primaryColor }}>{item.price} ₺</span>
                            </div>
                        );
                    })}
                </div>
                <MenuFooter tenant={tenant} primaryColor={primaryColor} textColor={textColor} />
                <ItemDetailModal item={selectedItem} onClose={() => setSelectedItem(null)} onAddToCart={onAddToCart} onUpdateQuantity={onUpdateQuantity} cart={cart} primaryColor={primaryColor} tenantPlan={tenantPlan} t={t} />
            </div>
        );
    }

    // ═══════════════════════════════════════════
    // TEMPLATE: CLASSIC (Varsayılan)
    // ═══════════════════════════════════════════

    // Items view
    if (selectedCategory || searchTerm) {
        const displayedItems = getDisplayedItems();
        const categoryTitle = selectedCategory || t('common.searchResults');

        return (
            <div className="fade-in" style={{ fontFamily: fontFamily, maxWidth: '600px', margin: '0 auto', width: '100%', '--text-main': textColor, '--text-muted': `${textColor}b3`, color: textColor }}>
                <style>{`
                    @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;700;900&family=Outfit:wght@400;700;900&family=Playfair+Display:ital,wght@0,400;0,700;0,900;1,400&display=swap');
                    * { font-family: inherit; }
                `}</style>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
                    <button onClick={handleManualBack} style={{ background: 'var(--bg-card)', border: 'none', padding: '10px', borderRadius: '12px', display: 'flex', alignItems: 'center', color: 'var(--text-main)', boxShadow: 'var(--shadow-sm)' }}>
                        <ArrowLeft size={18} />
                    </button>
                    <h2 style={{ fontSize: '1.25rem', fontWeight: 800 }}>{categoryTitle}</h2>
                </div>

                <div className="card glass-premium" style={{ padding: '1rem', marginBottom: '1.5rem' }}>
                    <div style={{ position: 'relative' }}>
                        <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                        <input type="text" placeholder={t('common.search')} value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                            style={{ paddingLeft: '40px', background: 'transparent', border: 'none', fontSize: '0.875rem', width: '100%', outline: 'none', color: 'inherit' }}
                        />
                    </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    {displayedItems.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '3rem', opacity: 0.5 }}>{t('common.noResults')}</div>
                    ) : displayedItems.map((item, idx) => {
                        const { prodId, isOutOfStock } = getStockInfo(item);
                        return (
                            <div key={`${prodId}-${idx}`} className="card-premium"
                                onClick={() => !isOutOfStock && setSelectedItem({ ...item })}
                                style={{ padding: '0', display: 'flex', flexDirection: 'column', opacity: isOutOfStock ? 0.7 : 1, overflow: 'hidden', cursor: isOutOfStock ? 'default' : 'pointer' }}>
                                {item.image && (
                                    <div style={{ width: '100%', aspectRatio: '1/1', overflow: 'hidden', position: 'relative', backgroundColor: 'rgba(0,0,0,0.05)' }}>
                                        <img src={item.image} alt={item.name} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', filter: isOutOfStock ? 'grayscale(100%)' : 'none' }} />
                                        {isOutOfStock && (
                                            <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '12px', fontWeight: 800, textAlign: 'center', textTransform: 'uppercase', padding: '4px' }}>
                                                {t('inventory.outOfStock', 'Stokta Yok')}
                                            </div>
                                        )}
                                    </div>
                                )}
                                <div style={{ padding: '1.25rem', flex: 1, display: 'flex', flexDirection: 'column' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '4px' }}>
                                        <div style={{ flex: 1, paddingRight: '1rem' }}>
                                            <h4 style={{ fontSize: '1.125rem', fontWeight: 900, color: 'var(--text-main)', margin: '0 0 4px 0' }}>{item.name}</h4>
                                            {item.description && <p style={{ fontSize: '0.875rem', color: 'var(--text-muted)', margin: 0, lineHeight: 1.5, opacity: 0.8, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', wordBreak: 'break-word' }}>{item.description}</p>}
                                        </div>
                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', flexShrink: 0 }}>
                                            <span style={{ fontWeight: 800, fontSize: '1.25rem', color: primaryColor }}>{item.price} ₺</span>
                                            <div style={{ marginTop: '0.5rem' }} onClick={e => e.stopPropagation()}>
                                                {renderCartButton(item, prodId, isOutOfStock, 'text-only')}
                                            </div>
                                        </div>
                                    </div>
                                    <AllergenBadges allergens={item.allergens} />
                                </div>
                            </div>
                        );
                    })}
                </div>

                <button onClick={() => setSelectedCategory(null)} style={{ position: 'fixed', bottom: '80px', right: '25px', width: '60px', height: '60px', borderRadius: '50%', background: primaryColor, color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 10px 25px -5px rgba(0,0,0,0.3)', border: 'none', zIndex: 100 }}>
                    <MenuIcon size={24} />
                </button>

                <MenuFooter tenant={tenant} primaryColor={primaryColor} textColor={textColor} />
                <ItemDetailModal item={selectedItem} onClose={() => setSelectedItem(null)} onAddToCart={onAddToCart} onUpdateQuantity={onUpdateQuantity} cart={cart} primaryColor={primaryColor} tenantPlan={tenantPlan} t={t} />
            </div>
        );
    }

    // LANDING: Category Grid (Classic)
    return (
        <div className="fade-in" style={{ fontFamily: fontFamily, maxWidth: '600px', margin: '0 auto', width: '100%', '--text-main': textColor, '--text-muted': `${textColor}b3`, color: textColor, '--bg-card': cardBgColor }}>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;700;900&family=Outfit:wght@400;700;900&family=Playfair+Display:ital,wght@0,400;0,700;0,900;1,400&display=swap');
                * { font-family: inherit; }
            `}</style>
            {!hideHeader && (
                <div style={{ textAlign: 'center', marginBottom: '1.5rem', padding: '0.5rem 1rem 0' }}>
                    <h2 style={{ fontSize: '1.125rem', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '2px', color: 'var(--text-main)', marginBottom: '0.25rem', display: 'flex', alignItems: 'center', justifyItems: 'center', justifyContent: 'center', gap: '10px' }}>
                        <Utensils size={16} style={{ color: primaryColor }} />
                        {menuData.menuName}
                        <Utensils size={16} style={{ color: primaryColor }} />
                    </h2>
                    {menuData.description && (
                        <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', fontStyle: 'italic', maxWidth: '320px', margin: '0.25rem auto 0' }}>{menuData.description}</p>
                    )}
                </div>
            )}

            <div className="card glass-premium" style={{ padding: '0.875rem', marginBottom: '1.5rem', background: 'var(--bg-card)' }}>
                <div style={{ position: 'relative' }}>
                    <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                    <input type="text" placeholder={t('common.search')} value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                        style={{ paddingLeft: '40px', background: 'transparent', border: 'none', fontSize: '0.875rem', width: '100%', outline: 'none', color: 'inherit' }}
                    />
                </div>
            </div>
            
            {/* Featured items (Öne Çıkanlar) */}
            {featuredItems.length > 0 && (
                <div style={{ marginBottom: '2.5rem', padding: '0 0.5rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '1rem', padding: '0 0.5rem' }}>
                        <Star size={16} style={{ color: '#f59e0b' }} />
                        <h3 style={{ fontSize: '0.875rem', fontWeight: 800, letterSpacing: '2px', color: primaryColor }}>{featuredTitle}</h3>
                    </div>
                    <div style={{ display: 'flex', gap: '1rem', overflowX: 'auto', WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none', paddingBottom: '0.5rem', paddingLeft: '0.5rem' }}>
                        <style>{`.classic-featured::-webkit-scrollbar { display: none; }`}</style>
                        <div className="classic-featured" style={{ display: 'flex', gap: '1rem' }}>
                            {featuredItems.map((item, idx) => {
                                const { prodId, isOutOfStock } = getStockInfo(item);
                                return (
                                    <div key={idx} 
                                        onClick={() => handleManualItemClick(item, prodId, isOutOfStock)}
                                        style={{ width: '160px', flexShrink: 0, cursor: 'pointer', opacity: isOutOfStock ? 0.6 : 1 }}>
                                        <div style={{ width: '100%', aspectRatio: '1/1', borderRadius: '18px', overflow: 'hidden', position: 'relative', background: 'rgba(0,0,0,0.05)', marginBottom: '0.75rem', transition: 'transform 0.2s' }}
                                            onMouseOver={e => e.currentTarget.style.transform = 'scale(1.02)'}
                                            onMouseOut={e => e.currentTarget.style.transform = 'scale(1)'}
                                        >
                                            <img src={item.image || 'https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?w=400&h=400&fit=crop'} alt={item.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                            <div style={{ position: 'absolute', top: '8px', right: '8px', background: 'white', borderRadius: '50%', width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                                                <Plus size={14} color={primaryColor} />
                                            </div>
                                            {isOutOfStock && (
                                                <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '10px', fontWeight: 800, textAlign: 'center', textTransform: 'uppercase' }}>
                                                    {t('inventory.outOfStock', 'Stokta Yok')}
                                                </div>
                                            )}
                                        </div>
                                        <div style={{ padding: '0 4px' }}>
                                            <h4 style={{ fontSize: '0.8125rem', fontWeight: 800, color: 'var(--text-main)', marginBottom: '4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name}</h4>
                                            <div style={{ fontSize: '0.9375rem', fontWeight: 900, color: primaryColor }}>{item.price} ₺</div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: '1.25rem' }}>
                {filteredCategories.map((cat, idx) => (
                    <div key={idx} className="card-premium fade-in-up" onClick={() => handleCategoryClick(cat.categoryName)}
                        style={{
                            padding: 0, overflow: 'hidden', position: 'relative', cursor: 'pointer',
                            animationDelay: `${idx * 0.05}s`, borderRadius: '24px', minWidth: 0,
                            backgroundColor: 'rgba(0,0,0,0.05)', display: 'flex', flexDirection: 'column'
                        }}>
                        <img src={cat.image || 'https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?w=400&h=600&fit=crop'} alt={cat.categoryName} style={{ width: '100%', height: 'auto', aspectRatio: '1/1', objectFit: 'cover' }} />
                        <div style={{
                            position: 'absolute', inset: 0,
                            background: 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.2) 60%, transparent 100%)',
                            display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', padding: '1.25rem'
                        }}>
                            <h3 style={{ color: 'white', fontSize: '0.8125rem', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '1px', textShadow: '0 2px 4px rgba(0,0,0,0.3)', margin: 0, minWidth: 0, overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
                                {cat.categoryName}
                            </h3>
                            <div style={{ width: '30px', height: '2px', background: 'white', marginTop: '0.5rem', opacity: 0.6, flexShrink: 0 }}></div>
                        </div>
                    </div>
                ))}
            </div>

            <MenuFooter tenant={tenant} primaryColor={primaryColor} textColor={textColor} />
            <ItemDetailModal item={selectedItem} onClose={() => setSelectedItem(null)} onAddToCart={onAddToCart} onUpdateQuantity={onUpdateQuantity} cart={cart} primaryColor={primaryColor} tenantPlan={tenantPlan} t={t} />
        </div>
    );
};

const MenuFooter = ({ tenant, primaryColor, textColor }) => {
    const { t } = useTranslation();
    if (!tenant) return null;

    const googleMapsUrl = tenant.address ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(tenant.address)}` : '#';

    return (
        <div style={{
            marginTop: '1.5rem', padding: '1.5rem 1rem', textAlign: 'center',
            borderTop: '1px solid rgba(128,128,128,0.1)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.75rem',
            background: 'var(--bg-card)', borderRadius: '40px 40px 0 0',
            width: '100%', maxWidth: '600px', margin: '1.5rem auto 0',
            boxShadow: '0 -10px 40px rgba(0,0,0,0.02)'
        }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                <h3 style={{ fontSize: '1.2rem', fontWeight: 900, letterSpacing: '2px', color: primaryColor, margin: 0 }}>{tenant.name}</h3>
                {tenant.address && (
                    <p style={{ fontSize: '0.925rem', opacity: 0.9, maxWidth: '350px', lineHeight: 1.6, color: primaryColor, margin: '0 auto' }}>
                        {tenant.address}
                    </p>
                )}
                {tenant.phone && (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', fontSize: '0.9rem', fontWeight: 700, color: primaryColor }}>
                        <Phone size={14} />
                        {tenant.phone}
                    </div>
                )}

                <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', marginTop: '0.25rem' }}>
                    {(tenant.socialMedia?.instagram || tenant.social_media?.instagram) && (
                        <a
                            href={`https://instagram.com/${(tenant.socialMedia?.instagram || tenant.social_media?.instagram).replace('@', '')}`}
                            target="_blank" rel="noopener noreferrer"
                            style={{
                                width: '38px', height: '38px', borderRadius: '50%',
                                background: 'linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white',
                                boxShadow: '0 4px 10px rgba(0,0,0,0.1)'
                            }}
                        >
                            <Instagram size={18} />
                        </a>
                    )}
                    {(tenant.googleMapsUrl || tenant.google_maps_url) && (
                        <a
                            href={tenant.googleMapsUrl || tenant.google_maps_url}
                            target="_blank" rel="noopener noreferrer"
                            style={{
                                width: '38px', height: '38px', borderRadius: '50%', background: '#4285F4',
                                display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white',
                                boxShadow: '0 4px 10px rgba(0,0,0,0.1)'
                            }}
                        >
                            <MapPin size={18} />
                        </a>
                    )}
                </div>
            </div>

            {tenant.address && (
                <a
                    href={googleMapsUrl} target="_blank" rel="noopener noreferrer" className="btn-premium"
                    style={{
                        padding: '12px 32px', borderRadius: '100px', border: `1px solid ${primaryColor}`,
                        color: primaryColor, fontSize: '0.9rem', fontWeight: 800, textDecoration: 'none',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)', textTransform: 'uppercase', letterSpacing: '1px'
                    }}
                    onMouseOver={(e) => { e.target.style.background = primaryColor; e.target.style.color = 'white'; e.target.style.boxShadow = `0 10px 20px -5px ${primaryColor}40`; }}
                    onMouseOut={(e) => { e.target.style.background = 'transparent'; e.target.style.color = primaryColor; e.target.style.boxShadow = 'none'; }}
                >
                    {t('common.getDirections', 'Yol Tarifi')}
                </a>
            )}

            <div style={{ marginTop: '2rem', fontSize: '0.75rem', opacity: 0.8, letterSpacing: '1px' }}>
                powered by <a href="https://ugra.io" target="_blank" rel="noopener noreferrer" style={{ color: '#3b82f6', textDecoration: 'none' }}>ugra.io</a>
            </div>
        </div>
    );
};

export default DigitalMenu;
